<script setup lang="ts">
import { IconExclamationCircle } from '@tabler/icons-vue'
import { useRoute } from 'vue-router'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'

import { stringOrUndefined } from '@/helpers/StringHelpers'

import PaymentReturnPage from '@/pages/PaymentReturnPage.vue'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageLayout from '@/components/templates/PageLayout.vue'

const route = useRoute()

const cartId = stringOrUndefined(route.query.cartId)
const purchaserId = stringOrUndefined(route.query.purchaserId)
const sourceType = stringOrUndefined(route.query.source_type)
const clientSecret = stringOrUndefined(route.query.payment_intent_client_secret)
// Nested confirmationPath (path + query) rides inside Stripe return_url; PaymentReturnPage round-trips it via URLSearchParams.
const confirmationPath = stringOrUndefined(route.query.confirmationPath)
const retryPath = stringOrUndefined(route.query.retryPath)
const hasValidParams = Boolean(cartId && sourceType && clientSecret && confirmationPath)

// Strip secrets from the address bar without router.replace — that would remount this route with an empty query and drop the in-memory settle props.
if (hasValidParams) history.replaceState(null, '', route.path)
</script>

<template>
  <PageLayout>
    <Suspense v-if="hasValidParams">
      <template #fallback>
        <StatusMessage class="processing-message" variant="info">
          <LoadingSpinner :size="16" />
          Processing your payment...
        </StatusMessage>
      </template>
      <PaymentReturnPage
        :cartId="cartId!"
        :clientSecret="clientSecret!"
        :confirmationPath="confirmationPath!"
        :purchaserId="purchaserId"
        :retryPath="retryPath"
        :sourceType="sourceType!"
      />
    </Suspense>

    <StatusMessage
      v-else
      :buttonLabel="retryPath ? 'Retry' : undefined"
      :dismissible="false"
      :icon="IconExclamationCircle"
      message="Payment could not be resumed. Please try again."
      variant="error"
      @button-click="retryPath && $router.push(retryPath)"
    />
  </PageLayout>
</template>

<style scoped>
.processing-message :deep(.message-body) {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}
</style>
